/* ==========================================================================
   DreamBrothers — db-glb.js
   Procedural GLB builder + shared helpers for on-the-fly 3D models.
   No manual GLB files ever: any image becomes a framed 3D artwork at runtime.
   Shared by db-configurator.js and db-3d-viewer.js.
   ========================================================================== */
(function () {
  'use strict';

  if (window.DBGlb) return;

  var MODEL_VIEWER_SRC = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js';
  var mvPromise = null;

  /* Build a realistic wall-art model.
     opts: { pngBytes, width:m, height:m, depth:m, mode:'canvas'|'poster',
             edgeColor:[r,g,b] 0..1 (poster frame side), roughness, mime }
     - canvas: gallery-wrap — the artwork continues over the 4 side faces
       (like a real stretched canvas on a bar). Back = light neutral.
     - poster: framed print — front artwork, coloured frame sides, kraft back. */
  function buildPosterGlb(opts) {
    var png = opts.pngBytes;
    var w = opts.width, h = opts.height, d = opts.depth;
    var mode = opts.mode === 'canvas' ? 'canvas' : 'poster';
    var edge = opts.edgeColor || [0.08, 0.08, 0.09];
    var rough = typeof opts.roughness === 'number' ? opts.roughness : 0.85;
    var imgMime = opts.mime || 'image/png';
    var hw = w / 2, hh = h / 2, hd = d / 2;
    // Mirror-wrap strips (fraction of the texture reflected onto the sides).
    // The FRONT is never cropped; the sides mirror the edge band outward.
    var sU = mode === 'canvas' ? Math.min(0.14, d / Math.max(w, 0.01)) : 0;
    var sV = mode === 'canvas' ? Math.min(0.14, d / Math.max(h, 0.01)) : 0;

    // Geometry groups by material: A=artwork(uv), B=canvas back, W=wood, E=edge, F=frame.
    var A = { pos: [], nor: [], uv: [], idx: [] };
    var B = { pos: [], nor: [], idx: [] };
    var W = { pos: [], nor: [], uv: [], idx: [] };
    var E = { pos: [], nor: [], idx: [] };
    var F = { pos: [], nor: [], idx: [] };
    var frameRgb = (opts.frameColor && opts.frameColor !== 'none') ? hexToRgb01(opts.frameColor) : null;
    var hasWood = mode === 'canvas' && opts.woodBytes;
    // Planar wood UVs (a tile every ~15cm), grain running along Y.
    var woodF = 1 / 0.15;
    function woodUV(c) { return c.map(function (p) { return [p[1] * woodF, p[0] * woodF]; }); }
    function quad(g, c, n, uv) {
      var base = g.pos.length / 3;
      for (var i = 0; i < 4; i++) {
        g.pos.push(c[i][0], c[i][1], c[i][2]);
        g.nor.push(n[0], n[1], n[2]);
        if (uv && g.uv) g.uv.push(uv[i][0], uv[i][1]);
      }
      g.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }

    // FRONT (artwork) — ALWAYS full bleed, never cropped. v=1 bottom, v=0 top.
    quad(A, [[-hw, -hh, hd], [hw, -hh, hd], [hw, hh, hd], [-hw, hh, hd]], [0, 0, 1],
      [[0, 1], [1, 1], [1, 0], [0, 0]]);

    // SIDES — canvas: MIRROR the front edge outward (front stays full; the side
    // reflects the edge band, so no white edge and no crop). Poster: solid edge.
    var sides = [
      { n: [1, 0, 0], c: [[hw, -hh, hd], [hw, -hh, -hd], [hw, hh, -hd], [hw, hh, hd]], uv: [[1, 1], [1 - sU, 1], [1 - sU, 0], [1, 0]] },
      { n: [-1, 0, 0], c: [[-hw, -hh, -hd], [-hw, -hh, hd], [-hw, hh, hd], [-hw, hh, -hd]], uv: [[sU, 1], [0, 1], [0, 0], [sU, 0]] },
      { n: [0, 1, 0], c: [[-hw, hh, hd], [hw, hh, hd], [hw, hh, -hd], [-hw, hh, -hd]], uv: [[0, 0], [1, 0], [1, sV], [0, sV]] },
      { n: [0, -1, 0], c: [[-hw, -hh, -hd], [hw, -hh, -hd], [hw, -hh, hd], [-hw, -hh, hd]], uv: [[0, 1 - sV], [1, 1 - sV], [1, 1], [0, 1]] }
    ];
    sides.forEach(function (s) {
      if (mode === 'canvas') quad(A, s.c, s.n, s.uv);
      else quad(E, s.c, s.n, null);
    });

    // BACK.
    function backRect(x0, x1, y0, y1, z) { return [[x1, y0, z], [x0, y0, z], [x0, y1, z], [x1, y1, z]]; }
    if (mode === 'canvas') {
      // Real Gelato/europosters stretched canvas, back structure from the outer
      // edge inward: (1) the printed side fabric FOLDS onto the back as a white
      // stapled flap, (2) a thin light-wood stretcher bar sits recessed behind
      // it (perimeter only, no centre brace), (3) the taut fabric back is
      // recessed deepest. The z-steps give the bars real visible depth.
      var pfold = Math.min(0.011, Math.min(w, h) * 0.03); // PRINTED design wrap onto back (~1cm, visible)
      var flap = Math.min(0.02, Math.min(w, h) * 0.055);   // white stapled canvas fold
      var barW = Math.min(0.02, Math.min(w, h) * 0.05);    // wood bar width
      var woodDrop = Math.min(0.006, d * 0.28);            // wood behind the flap
      var recess = Math.min(0.02, d * 0.62);               // fabric back deepest
      var zOut = -hd, zWood = -hd + woodDrop, zCan = -hd + recess;
      // ring boundaries from the outer edge inward: pfold (printed) -> flap (white) -> wood -> fabric
      var pL = -hw + pfold, pR = hw - pfold, pB = -hh + pfold, pT = hh - pfold;
      var fL = pL + flap, fR = pR - flap, fB = pB + flap, fT = pT - flap;
      var cL = fL + barW, cR = fR - barW, cB = fB + barW, cT = fT - barW;
      var wq = function (c) { quad(W, c, [0, 0, -1], hasWood ? woodUV(c) : null); };
      var wwall = function (c, n) { quad(W, c, n, hasWood ? woodUV(c) : null); };
      // 0) PRINTED design folded a few mm onto the back edge (mirrors the side band),
      //    exactly like the real Gelato/europosters wrap. Material A (artwork).
      //    UV uses the extreme edge band so the printed colours continue.
      quad(A, backRect(-hw, hw, pT, hh, zOut), [0, 0, -1], [[1, 1 - sV], [0, 1 - sV], [0, 1], [1, 1]]);
      quad(A, backRect(-hw, hw, -hh, pB, zOut), [0, 0, -1], [[1, 0], [0, 0], [0, sV], [1, sV]]);
      quad(A, backRect(-hw, pL, pB, pT, zOut), [0, 0, -1], [[sU, 0], [0, 0], [0, 1], [sU, 1]]);
      quad(A, backRect(pR, hw, pB, pT, zOut), [0, 0, -1], [[1, 0], [1 - sU, 0], [1 - sU, 1], [1, 1]]);
      // 1) white stapled canvas flap (the raw fabric folded + stapled onto the back)
      quad(B, backRect(-pR, pR, fT, pT, zOut), [0, 0, -1], null);
      quad(B, backRect(-pR, pR, pB, fB, zOut), [0, 0, -1], null);
      quad(B, backRect(pL, fL, fB, fT, zOut), [0, 0, -1], null);
      quad(B, backRect(fR, pR, fB, fT, zOut), [0, 0, -1], null);
      // flap folds down to the wood (thin fabric walls)
      quad(B, [[fR, fT, zOut], [fL, fT, zOut], [fL, fT, zWood], [fR, fT, zWood]], [0, -1, 0], null);
      quad(B, [[fL, fB, zOut], [fR, fB, zOut], [fR, fB, zWood], [fL, fB, zWood]], [0, 1, 0], null);
      quad(B, [[fL, fT, zOut], [fL, fB, zOut], [fL, fB, zWood], [fL, fT, zWood]], [1, 0, 0], null);
      quad(B, [[fR, fB, zOut], [fR, fT, zOut], [fR, fT, zWood], [fR, fB, zWood]], [-1, 0, 0], null);
      // 2) light-wood stretcher bars (recessed → visible depth)
      wq(backRect(fL, fR, cT, fT, zWood)); // top bar
      wq(backRect(fL, fR, fB, cB, zWood)); // bottom bar
      wq(backRect(fL, cL, cB, cT, zWood)); // left bar
      wq(backRect(cR, fR, cB, cT, zWood)); // right bar
      // inner wood wall dropping to the central fabric
      wwall([[cR, cT, zWood], [cL, cT, zWood], [cL, cT, zCan], [cR, cT, zCan]], [0, -1, 0]);
      wwall([[cL, cB, zWood], [cR, cB, zWood], [cR, cB, zCan], [cL, cB, zCan]], [0, 1, 0]);
      wwall([[cL, cT, zWood], [cL, cB, zWood], [cL, cB, zCan], [cL, cT, zCan]], [1, 0, 0]);
      wwall([[cR, cB, zWood], [cR, cT, zWood], [cR, cT, zCan], [cR, cB, zCan]], [-1, 0, 0]);
      // 3) taut canvas fabric back (deepest)
      quad(B, backRect(cL, cR, cB, cT, zCan), [0, 0, -1], null);
    } else {
      quad(B, backRect(-hw, hw, -hh, hh, -hd), [0, 0, -1], null); // poster flat back
    }

    // OPTIONAL FLOATING FRAME (Gelato style) — a THIN profile with a visible
    // air gap (channel) between the canvas and the frame, so the art appears to
    // float inside. Black / White / Oak / Walnut.
    if (frameRgb) {
      var fw = Math.min(0.013, Math.max(w, h) * 0.02);  // THIN frame profile face
      var gap = Math.min(0.009, Math.max(w, h) * 0.016); // floating air gap
      var pop = Math.min(0.008, d * 0.5);                // frame proud of the art front
      var chD = Math.min(0.014, d * 0.85);               // channel floor recess (dark gap depth)
      var iW = hw + gap, iH = hh + gap;                  // inner wall (channel outer edge)
      var OW = iW + fw, OH = iH + fw;                    // outer extents
      var zF = hd + pop, zCh = hd - chD, zB = -hd;
      var fr = function (c, n) { quad(F, c, n, null); };
      // channel floor (dark, recessed) around the art → the floating gap
      fr(backReq(-iW, iW, hh, iH, zCh), [0, 0, 1]);
      fr(backReq(-iW, iW, -iH, -hh, zCh), [0, 0, 1]);
      fr(backReq(-iW, -hw, -hh, hh, zCh), [0, 0, 1]);
      fr(backReq(hw, iW, -hh, hh, zCh), [0, 0, 1]);
      // frame inner wall (channel floor → front lip)
      fr([[iW, -iH, zCh], [iW, -iH, zF], [iW, iH, zF], [iW, iH, zCh]], [-1, 0, 0]);
      fr([[-iW, iH, zCh], [-iW, iH, zF], [-iW, -iH, zF], [-iW, -iH, zCh]], [1, 0, 0]);
      fr([[-iW, iH, zCh], [iW, iH, zCh], [iW, iH, zF], [-iW, iH, zF]], [0, -1, 0]);
      fr([[iW, -iH, zCh], [-iW, -iH, zCh], [-iW, -iH, zF], [iW, -iH, zF]], [0, 1, 0]);
      // thin frame front lip
      fr(backReq(-OW, OW, iH, OH, zF), [0, 0, 1]);
      fr(backReq(-OW, OW, -OH, -iH, zF), [0, 0, 1]);
      fr(backReq(-OW, -iW, -iH, iH, zF), [0, 0, 1]);
      fr(backReq(iW, OW, -iH, iH, zF), [0, 0, 1]);
      // frame outer wall (front lip → back)
      fr([[OW, -OH, zF], [OW, -OH, zB], [OW, OH, zB], [OW, OH, zF]], [1, 0, 0]);
      fr([[-OW, OH, zF], [-OW, OH, zB], [-OW, -OH, zB], [-OW, -OH, zF]], [-1, 0, 0]);
      fr([[-OW, OH, zF], [OW, OH, zF], [OW, OH, zB], [-OW, OH, zB]], [0, 1, 0]);
      fr([[OW, -OH, zF], [-OW, -OH, zF], [-OW, -OH, zB], [OW, -OH, zB]], [0, -1, 0]);
      // frame back
      fr(backRect(-OW, OW, iH, OH, zB), [0, 0, -1]);
      fr(backRect(-OW, OW, -OH, -iH, zB), [0, 0, -1]);
      fr(backRect(-OW, -iW, -iH, iH, zB), [0, 0, -1]);
      fr(backRect(iW, OW, -iH, iH, zB), [0, 0, -1]);
    }
    // front-facing border rects use CCW winding for +Z normal
    function backReq(x0, x1, y0, y1, z) { return [[x0, y0, z], [x1, y0, z], [x1, y1, z], [x0, y1, z]]; }

    // Assemble buffer + accessors dynamically from the used groups.
    var parts = [], offset = 0, accessors = [], bufferViews = [], primitives = [];
    function pushView(ta) {
      var bytes = new Uint8Array(ta.buffer, ta.byteOffset, ta.byteLength);
      var idx = bufferViews.length;
      bufferViews.push({ buffer: 0, byteOffset: offset, byteLength: bytes.byteLength });
      parts.push(bytes); offset += bytes.byteLength;
      var pad = (4 - (offset % 4)) % 4; if (pad) { parts.push(new Uint8Array(pad)); offset += pad; }
      return idx;
    }
    function minMax(arr) {
      var mn = [Infinity, Infinity, Infinity], mx = [-Infinity, -Infinity, -Infinity];
      for (var i = 0; i < arr.length; i += 3) for (var k = 0; k < 3; k++) { if (arr[i + k] < mn[k]) mn[k] = arr[i + k]; if (arr[i + k] > mx[k]) mx[k] = arr[i + k]; }
      return { min: mn, max: mx };
    }
    function addGroup(g, material, withUv) {
      var n = g.pos.length / 3;
      var mm = minMax(g.pos);
      var bvPos = pushView(new Float32Array(g.pos));
      var bvNor = pushView(new Float32Array(g.nor));
      var attributes = {};
      attributes.POSITION = accessors.length;
      accessors.push({ bufferView: bvPos, componentType: 5126, count: n, type: 'VEC3', min: mm.min, max: mm.max });
      attributes.NORMAL = accessors.length;
      accessors.push({ bufferView: bvNor, componentType: 5126, count: n, type: 'VEC3' });
      if (withUv) {
        var bvUv = pushView(new Float32Array(g.uv));
        attributes.TEXCOORD_0 = accessors.length;
        accessors.push({ bufferView: bvUv, componentType: 5126, count: n, type: 'VEC2' });
      }
      var bvIdx = pushView(new Uint16Array(g.idx));
      var idxAcc = accessors.length;
      accessors.push({ bufferView: bvIdx, componentType: 5123, count: g.idx.length, type: 'SCALAR' });
      primitives.push({ attributes: attributes, indices: idxAcc, material: material });
    }

    // material indices: 0 artwork, 1 canvasback(cream), 2 wood, 3 edge(poster), 4 frame
    addGroup(A, 0, true);
    if (mode === 'canvas') addGroup(W, 2, hasWood);
    else addGroup(E, 3, false);
    addGroup(B, 1, false);
    if (frameRgb) addGroup(F, 4, false);
    var bvImage = pushView(png);
    var bvWood = hasWood ? pushView(opts.woodBytes) : -1;

    var binLength = offset;
    var bin = new Uint8Array(binLength), cursor = 0;
    parts.forEach(function (p) { bin.set(p, cursor); cursor += p.byteLength; });

    var cream = [0.90, 0.88, 0.83], wood = [0.80, 0.68, 0.50];
    var woodPbr = hasWood
      ? { baseColorTexture: { index: 1 }, metallicFactor: 0, roughnessFactor: 0.7 }
      : { baseColorFactor: [wood[0], wood[1], wood[2], 1], metallicFactor: 0, roughnessFactor: 0.75 };
    var materials = [
      { name: 'artwork', pbrMetallicRoughness: { baseColorTexture: { index: 0 }, metallicFactor: 0, roughnessFactor: rough } },
      { name: 'canvasback', doubleSided: true, pbrMetallicRoughness: { baseColorFactor: [cream[0], cream[1], cream[2], 1], metallicFactor: 0, roughnessFactor: 0.95 } },
      { name: 'wood', doubleSided: true, pbrMetallicRoughness: woodPbr }
    ];
    materials.push({ name: 'edge', doubleSided: true, pbrMetallicRoughness: { baseColorFactor: [edge[0], edge[1], edge[2], 1], metallicFactor: 0.05, roughnessFactor: 0.6 } });
    if (frameRgb) {
      var isWhiteFrame = (frameRgb[0] + frameRgb[1] + frameRgb[2]) > 2.4;
      materials.push({ name: 'frame', pbrMetallicRoughness: { baseColorFactor: [frameRgb[0], frameRgb[1], frameRgb[2], 1], metallicFactor: 0, roughnessFactor: isWhiteFrame ? 0.5 : 0.4 } });
    }

    var images = [{ bufferView: bvImage, mimeType: imgMime }];
    var textures = [{ source: 0, sampler: 0 }];
    var samplers = [{ magFilter: 9729, minFilter: 9729, wrapS: 33071, wrapT: 33071 }];
    if (hasWood) {
      images.push({ bufferView: bvWood, mimeType: 'image/png' });
      samplers.push({ magFilter: 9729, minFilter: 9987, wrapS: 10497, wrapT: 10497 });
      textures.push({ source: 1, sampler: 1 });
    }
    var gltf = {
      asset: { version: '2.0', generator: 'DreamBrothers Configurator' },
      scene: 0, scenes: [{ nodes: [0] }], nodes: [{ mesh: 0, name: 'CustomArtwork' }],
      meshes: [{ primitives: primitives }],
      materials: materials,
      textures: textures,
      samplers: samplers,
      images: images,
      accessors: accessors,
      bufferViews: bufferViews,
      buffers: [{ byteLength: binLength }]
    };

    var jsonBytes = new TextEncoder().encode(JSON.stringify(gltf));
    var jsonPad = (4 - (jsonBytes.byteLength % 4)) % 4;
    var jsonLength = jsonBytes.byteLength + jsonPad;
    var binPad = (4 - (binLength % 4)) % 4;
    var binChunkLength = binLength + binPad;
    var total = 12 + 8 + jsonLength + 8 + binChunkLength;
    var out = new ArrayBuffer(total);
    var dv = new DataView(out), u8 = new Uint8Array(out);
    dv.setUint32(0, 0x46546c67, true); dv.setUint32(4, 2, true); dv.setUint32(8, total, true);
    dv.setUint32(12, jsonLength, true); dv.setUint32(16, 0x4e4f534a, true);
    u8.set(jsonBytes, 20);
    for (var i = 0; i < jsonPad; i++) u8[20 + jsonBytes.byteLength + i] = 0x20;
    var binStart = 20 + jsonLength;
    dv.setUint32(binStart, binChunkLength, true); dv.setUint32(binStart + 4, 0x004e4942, true);
    u8.set(bin, binStart + 8);
    return out;
  }

  /* Lazy-load Google model-viewer once, only when a 3D view opens. */
  function loadModelViewer() {
    if (window.customElements && customElements.get('model-viewer')) return Promise.resolve();
    if (!mvPromise) {
      mvPromise = new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.type = 'module';
        script.src = MODEL_VIEWER_SRC;
        script.onload = function () { resolve(); };
        script.onerror = function () { mvPromise = null; reject(new Error('model-viewer load failed')); };
        document.head.appendChild(script);
      });
    }
    return mvPromise;
  }

  /* Load an image (CORS-safe for canvas) → resolves HTMLImageElement. */
  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('image load failed: ' + src)); };
      img.src = src;
    });
  }

  /* Canvas → PNG bytes. */
  function canvasToPngBytes(canvas) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (!blob) { reject(new Error('toBlob failed')); return; }
        blob.arrayBuffer().then(function (buf) { resolve(new Uint8Array(buf)); }, reject);
      }, 'image/png');
    });
  }

  function hexToRgb01(hex) {
    var m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
    if (!m) return [0.1, 0.1, 0.1];
    var n = parseInt(m[1], 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }

  // Force a clean front-facing framing once the model is ready. The initial
  // camera-orbit attribute alone can be overridden by model-viewer auto-framing.
  function frameFront(mv) {
    // model-viewer's DEFAULT camera already looks at the textured front face
    // (+Z). We must NOT override camera-orbit — doing so was what spun the
    // model onto its back. Just hide until the first paint, then fade in and
    // start the gentle spin from the front default.
    mv.style.opacity = '0';
    mv.style.transition = 'opacity 0.3s ease';
    var wantsRotate = mv.hasAttribute('auto-rotate');
    mv.removeAttribute('auto-rotate');
    mv.addEventListener('load', function () {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          mv.style.opacity = '1';
          if (wantsRotate) {
            mv.setAttribute('auto-rotate-delay', '900');
            mv.setAttribute('auto-rotate', '');
          }
        });
      });
    }, { once: true });
  }

  window.DBGlb = {
    buildPosterGlb: buildPosterGlb,
    loadModelViewer: loadModelViewer,
    loadImage: loadImage,
    canvasToPngBytes: canvasToPngBytes,
    hexToRgb01: hexToRgb01,
    frameFront: frameFront
  };
  // kept for the automated structural tests
  window.__dbBuildPosterGlb = buildPosterGlb;
})();
